/**
 * Les chemins de la vitrine — [DECISIONS §190, §194].
 *
 * **Une fonction pure, et c'est délibéré.** `App.tsx` est le fichier le plus
 * disputé du dépôt : la vitrine y tient en une ligne, et toute la connaissance
 * de « quel chemin m'appartient » vit ici, où elle se teste sans monter un seul
 * composant.
 */

/**
 * Un `public_id` d'item — le MÊME motif que la route du serveur.
 *
 * Un ULID : 26 caractères de l'alphabet de Crockford (base 32, sans i, l, o ni
 * u), écrits en minuscules par `HasUlids`. Le serveur refuse déjà tout le
 * reste avant d'entrer dans le contrôleur (§190) ; l'écran refuse avant
 * d'entrer sur le réseau. Les deux motifs doivent rester identiques — le jour
 * où l'un s'élargit sans l'autre, l'écran promet une page que le serveur
 * n'ouvrira pas, ou l'inverse.
 */
const IDENTIFIANT_OPAQUE = /^[0-9a-hjkmnp-tv-z]{26}$/

/**
 * L'adresse du formulaire de compte — [DECISIONS §194].
 *
 * L'accueil ayant pris `/`, l'inscription et la connexion ont besoin d'une
 * adresse à elles. **La vitrine ne la sert pas** : `routeDeLaVitrine` rend
 * `null` dessus, la coque prend la main, et un visiteur sans session y trouve
 * l'écran qui existait déjà. Elle n'est ici que pour être écrite au même
 * endroit que les autres chemins.
 */
export const CHEMIN_DU_COMPTE = '/compte'

/**
 * La même adresse, mais sur l'onglet « Se connecter » — [DECISIONS §220].
 *
 * **Le drapeau passe par l'adresse parce que le départ recharge la page.** La
 * vitrine n'a pas de routeur : `rechargerVers` fait un vrai changement de
 * document, et aucun état de React ne survit. Sans ce paramètre, le visiteur
 * qui tape « Se connecter » atterrissait sur « Créer un compte » — c'est-à-dire
 * sur un formulaire de six champs alors qu'il en voulait deux, et il devait
 * comprendre tout seul qu'un second onglet existait.
 *
 * Un paramètre nu plutôt qu'une valeur : `?se-connecter` se lit dans la barre
 * d'adresse, et il n'y a rien à analyser.
 */
export const CHEMIN_DE_LA_CONNEXION = '/compte?se-connecter'

/** Le visiteur arrive-t-il pour se connecter plutôt que pour s'inscrire ? */
export function veutSeConnecter(recherche: string): boolean {
  return new URLSearchParams(recherche).has('se-connecter')
}

/**
 * L'adresse de « payer pour quelqu'un » — [DECISIONS §197].
 *
 * `/offrir`, et `/offrir/{public_id}` pour le reçu de celui qui revient de
 * MonCash. Une seule racine pour les deux : le payeur n'a pas de compte, donc
 * pas d'historique — le seul endroit d'où il peut revenir est le lien qu'il a
 * gardé, et il doit ressembler à celui d'où il est parti.
 */
export const CHEMIN_DU_CADEAU = '/offrir'

/**
 * L'adresse de la méthode — [DECISIONS §220].
 *
 * « Tu peux être prêt » est une affirmation, et une affirmation se justifie.
 * Elle a son adresse propre plutôt qu'un dépliant sur l'accueil : le texte est
 * long, il se lit d'un bout à l'autre, et il doit pouvoir se partager tel quel
 * — un professeur qui le fait lire à sa classe envoie un lien, pas une consigne
 * de défilement.
 */
export const CHEMIN_DE_LA_METHODE = '/methode'

/**
 * La racine des pages d'archives — 25 §3.7.
 *
 * `/archives/{faculté}/{année}/{matière}`, par exemple
 * `/archives/fasch/2018/culture-generale`. **Les mots de la requête sont dans
 * l'adresse** : « concours FASCH culture générale 2018 » est ce qu'un candidat
 * tape vraiment, et cette requête n'avait aucune page à rencontrer — la seule
 * adresse publique du corpus était `/q/{ulid}`, où rien ne se lit.
 */
export const CHEMIN_DES_ARCHIVES = '/archives'

/**
 * La racine des liens de partage qui expirent — Ben, le 2 septembre 2026.
 *
 * `/p/{jeton}`. **Court exprès** : ce lien se colle dans un message, se lit à
 * voix haute et se retape parfois à la main.
 *
 * **Pourquoi pas `/q/`.** Cette racine-là sert la vitrine indexée d'une
 * question, elle est déclarée au plan du site et elle ne meurt jamais. Un lien
 * qui expire au bout de deux semaines n'a rien à faire dans un index de moteur
 * de recherche : deux objets différents, deux racines différentes.
 */
export const CHEMIN_DU_LIEN = '/p'

/**
 * Le jeton d'un lien de partage — le MÊME motif que la route du serveur.
 *
 * Un ULID écrit en MAJUSCULES, contrairement au `public_id` d'un item : celui-ci
 * est frappé par le service au moment d'émettre, pas par `HasUlids`. Les deux
 * motifs doivent rester identiques de part et d'autre du réseau.
 */
const JETON_DE_LIEN = /^[0-9A-HJKMNP-TV-Z]{26}$/

/** Le triplet qu'une adresse d'archives désigne. */
export interface TripletDArchives {
  faculte: string
  annee: number
  /** La forme de l'ADRESSE — des tirets, jamais des soulignés. */
  matiere: string
}

/**
 * Les trois motifs du serveur, recopiés à l'identique.
 *
 * `Modules/Vitrine/Routes/api.php` les pose sur sa route ; l'écran les pose
 * avant d'entrer sur le réseau. Ils doivent rester identiques — le jour où
 * l'un des deux s'élargit sans l'autre, l'écran promet une page que le serveur
 * n'ouvrira pas, ou l'inverse.
 *
 * **La matière n'admet pas le souligné**, et c'est toute la raison du motif :
 * le référentiel écrit `culture_generale`, l'adresse écrit `culture-generale`,
 * parce qu'un moteur coupe les mots sur le tiret et pas sur le souligné.
 * Accepter les deux formes donnerait deux adresses au même contenu — le défaut
 * que 25 §1.6 répare entre `www` et l'apex, refabriqué à l'intérieur du site.
 */
const FACULTE = /^[a-z]{2,32}$/
const ANNEE = /^(19|20)[0-9]{2}$/
const MATIERE = /^[a-z][a-z0-9-]{0,119}$/

export type RouteDeLaVitrine =
  | { genre: 'accueil' }
  /**
   * `publicId` vaut `null` quand ce qui suit `/q/` n'est pas un identifiant —
   * un lien coupé en deux par une messagerie, par exemple. La page le dit
   * alors sans rien demander au serveur.
   */
  | { genre: 'question'; publicId: string | null }
  /**
   * `paiementId` vaut `null` sur `/offrir` — l'écran de saisie. Renseigné, il
   * désigne le reçu d'un paiement déjà ouvert.
   */
  | { genre: 'cadeau'; paiementId: string | null }
  /** La présentation de la méthode et de ce qui la fonde — §220. */
  | { genre: 'methode' }
  /**
   * `jeton` vaut `null` quand ce qui suit `/p/` n'en est pas un — un lien
   * coupé par une messagerie, une lettre perdue à la recopie. La page le dit
   * alors sans rien demander au serveur, et sans laisser croire à une
   * expiration : un lien malformé n'a pas expiré, il n'a jamais existé.
   */
  | { genre: 'lien'; jeton: string | null }
  /**
   * `triplet` vaut `null` quand ce qui suit `/archives/` n'en désigne aucun —
   * une adresse tronquée, une année à cinq chiffres, un souligné à la place du
   * tiret. La page le dit alors sans rien demander au serveur, et se retire de
   * l'index elle-même (25 §3.5).
   */
  | { genre: 'archives'; triplet: TripletDArchives | null }

/**
 * À qui appartient ce chemin — `null` quand c'est à l'application.
 *
 * **Tout `/q/…` appartient à la vitrine**, y compris un identifiant tronqué.
 * Le laisser retomber sur la coque aurait affiché le formulaire de connexion à
 * qui suit un lien partagé — ce qui se lit comme une demande de payer pour
 * voir, alors que la page est justement gratuite.
 *
 * **Et tout `/offrir…` aussi, y compris avec une session ouverte** (§197). Le
 * parent qui paie emprunte souvent le téléphone où l'enfant est connecté ;
 * rendre la coque à cette adresse afficherait le fil de l'enfant à qui voulait
 * payer. Un identifiant de reçu tronqué reste ici pour la même raison qu'une
 * question tronquée : c'est la page du cadeau qui doit le dire, pas un
 * formulaire de connexion.
 */
export function routeDeLaVitrine(chemin: string): RouteDeLaVitrine | null {
  if (chemin === '' || chemin === '/') return { genre: 'accueil' }

  if (chemin === '/q' || chemin.startsWith('/q/')) {
    // Une messagerie ajoute volontiers une barre finale au lien qu'elle
    // détecte : `/q/{ulid}/` désigne la même question.
    const brut = chemin.slice(3).replace(/\/+$/, '')

    return { genre: 'question', publicId: IDENTIFIANT_OPAQUE.test(brut) ? brut : null }
  }

  /**
   * **Tout `/p/…` appartient à la vitrine, connecté ou non.** Le rendre à la
   * coque afficherait le formulaire de connexion à qui suit un lien qu'on lui
   * a envoyé — c'est-à-dire une demande de payer pour voir, sur une page
   * justement ouverte à tous. Et chez un candidat connecté, la même adresse
   * aurait affiché SON FIL au lieu du contenu partagé.
   */
  if (chemin === CHEMIN_DU_LIEN || chemin.startsWith(`${CHEMIN_DU_LIEN}/`)) {
    const brut = chemin.slice(CHEMIN_DU_LIEN.length).replace(/^\/+|\/+$/g, '')

    return { genre: 'lien', jeton: JETON_DE_LIEN.test(brut) ? brut : null }
  }

  // `/methode` et `/methode/` : une barre finale ajoutée par une messagerie
  // ne doit pas renvoyer le lecteur sur le formulaire de connexion.
  if (chemin === CHEMIN_DE_LA_METHODE || chemin === `${CHEMIN_DE_LA_METHODE}/`) {
    return { genre: 'methode' }
  }

  /**
   * **Tout `/archives…` appartient à la vitrine, connecté ou non** — 25 §3.7.
   *
   * Ces pages viennent d'un moteur : celui qui les ouvre n'a pas de compte
   * neuf fois sur dix, et le rendre à la coque lui afficherait le formulaire
   * de connexion sur un résultat de recherche — c'est-à-dire une demande de
   * payer pour voir, sur une page qui est gratuite. Et chez un candidat
   * connecté, la même adresse aurait affiché SON FIL : un lien qui n'ouvre pas
   * ce qu'il désigne n'est pas une adresse publique.
   *
   * **L'écran authentifié des archives, lui, n'a pas d'adresse** — il vit dans
   * un onglet de la coque. Il n'y a donc aucune collision à arbitrer.
   */
  if (chemin === CHEMIN_DES_ARCHIVES || chemin.startsWith(`${CHEMIN_DES_ARCHIVES}/`)) {
    return { genre: 'archives', triplet: tripletDArchives(chemin) }
  }

  if (chemin === CHEMIN_DU_CADEAU || chemin.startsWith(`${CHEMIN_DU_CADEAU}/`)) {
    const brut = chemin.slice(CHEMIN_DU_CADEAU.length).replace(/^\/+|\/+$/g, '')

    // `/offrir` et `/offrir/` mènent au même écran de saisie ; le motif est le
    // MÊME que celui du serveur, qui refuse déjà tout le reste avant d'entrer
    // dans le contrôleur. Le jour où l'un des deux s'élargit sans l'autre,
    // l'écran promet une page que le serveur n'ouvrira pas.
    return { genre: 'cadeau', paiementId: IDENTIFIANT_OPAQUE.test(brut) ? brut : null }
  }

  return null
}

/**
 * Le triplet que porte un chemin d'archives — `null` quand il n'en porte pas.
 *
 * **Trois segments exactement.** `/archives`, `/archives/fasch` et
 * `/archives/fasch/2018/culture-generale/en-plus` n'en désignent aucun : ce
 * sont des adresses qu'une messagerie a coupées, qu'un moteur a devinées, ou
 * qu'on a tapées à la main. Les laisser passer ferait demander au serveur une
 * page qui n'existe pas, et surtout ferait de chacune une page indexable de
 * plus — c'est le défaut du §1.7, reproduit à l'intérieur de la SPA.
 *
 * **La barre finale est tolérée**, comme sur `/q/` et `/methode` : une
 * messagerie l'ajoute volontiers au lien qu'elle détecte.
 */
function tripletDArchives(chemin: string): TripletDArchives | null {
  const segments = chemin
    .slice(CHEMIN_DES_ARCHIVES.length)
    .replace(/^\/+|\/+$/g, '')
    .split('/')

  if (segments.length !== 3) return null

  const [faculte, annee, matiere] = segments as [string, string, string]

  if (!FACULTE.test(faculte) || !ANNEE.test(annee) || !MATIERE.test(matiere)) return null

  return { faculte, annee: Number(annee), matiere }
}
